import React from "react";
import { Link } from "react-router-dom";
import { Icon, PlainButton, Button } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { ClassYearLink, DepartmentLink, LocationLink, ProgramLink, DegreeLink } from "app/links";
import CompanyLink from "app/links/CompanyLink";
import { canViewSkillsField } from "app/permissions";
import { t, Trans } from "app/utils";

import EllipisWithTooltip from "components/common/EllipisWithTooltip";
import { DateFormat, formatDate } from "components/common/Time";
import BuildConnection from "components/person/BuildConnection";
import UserCommonalities from "components/community/UserCommonalities";
import ImageLightbox from "components/display/ImageLightbox";
import Tags from "components/person/Tags";
import QuickLinks from "components/person/QuickLinks";
import SocialLinks from "components/person/SocialLinks";

import getUserName from "app/utils/getUserName";
import styles from "./styles.module.scss";

const PersonLeftInfo = ({ onChange, user }) => {
  const { profile } = useProfile();
  const labels = useFieldsLabels();
  const { organization } = profile;
  const { isEducational } = organization;
  const { clubs, interests, skills } = profile;
  const isMyProfile = user.id === profile.id;

  return (
    <div className="Left-nav--Container">
      <ImageLightbox image={user.avatar}>
        {onTrigger => (
          <PlainButton
            className="person-image-wrapper"
            style={{ backgroundImage: `url(${user.avatar.mediumUrl})` }}
            onClick={onTrigger}
          />
        )}
      </ImageLightbox>
      <h1 className="Person--name">{getUserName(user)}</h1>
      {!isEducational && (<h2 className="Person--title">{user.title}</h2>)}
      {isEducational && (<h2 className="Person--biography">{user.biography}</h2>)}
      {user.id !== profile.id && (
        <div className="Commonalities-Container">
          <span className={styles.common}>
            <UserCommonalities connection={user.connection} showNumberOfCommonalities user={user} />
          </span>
        </div>
      )}

      <div className="Commonalities-Actions">
        {
          (isMyProfile)
            ? (
              <Link to="/profile/settings">
                <Button>
                  <Trans>Edit Profile</Trans>
                </Button>
              </Link>
            ) : (<BuildConnection profile={user} />)
        }
        {(user.social?.length > 0 || user.slackId)
          && (<SocialLinks noText onChange={onChange} user={user} />)}
        <QuickLinks noText onChange={onChange} profile={profile} user={user} />
      </div>

      <div className="Section__title">
        <h2 className="subtitle"><Trans>HIGHLIGHTS</Trans></h2>
      </div>
      <div className="Section__highlights">
        <p className="Section__highlights--email">
          <Icon icon="envelope" />
          <EllipisWithTooltip tip={user.email}>
            <a aria-label={t(`Send email to ${user.email}`)} href={`mailto:${user.email}`}>
              {user.email}
            </a>
          </EllipisWithTooltip>
        </p>
        {user.startedOn && (<p aria-label={t(`Start date at company: ${formatDate(user.startedOn)}`)}><Icon icon="clock" /><DateFormat time={user.startedOn} /></p>)}
        {user.classYear && (
          <p aria-label={t(`${organization.name === "Dartmouth Guarini" ? "Years in Program" : "Class year"}: ${user.classYear}`)}>
            <Icon icon="clock" />
            <ClassYearLink classYear={user.classYear} />
          </p>
        )}
        {user.location && (<p aria-label={t(`Location:  ${user.location.name}`)}><Icon icon="loc-ol" /><LocationLink location={user.location} /></p>)}
        {user.departments.length > 0 && (
          <p aria-label={`${t(labels.departments.plural)}:`}>
            <Icon icon="ios-people-outline" />
            <span>
              {user.departments.map((department, index) => (
                <span key={department.id}>
                  <DepartmentLink department={department} />
                  {index !== user.departments.length - 1 && ", "}
                </span>
              ))}
            </span>
          </p>
        )}
        {user.companies.length > 0 && (
          <p aria-label={`${t(labels.companies.plural)}:`}>
            <Icon icon="building" />
            <span>
              {user.companies.map((company, index) => (
                <span key={company.id}>
                  <CompanyLink company={company} />
                  {index !== user.companies.length - 1 && ", "}
                </span>
              ))}
            </span>
          </p>
        )}
        {user.degree && (<p aria-label={t(`Degree:  ${user.degree}`)}><Icon icon="mortarboard" /><DegreeLink degree={user.degree} /></p>)}
        {user.programs?.length > 0 && (
          <p aria-label={t("Programs:")}>
            <Icon icon="ios-star-outline" />
            <span>
              {user.programs.map((program, index) => (
                <span key={program.id}>
                  <ProgramLink program={program} />
                  {index !== user.programs.length - 1 && ", "}
                </span>
              ))}
            </span>
          </p>
        )}
      </div>

      {user.interests.length !== 0 && (
        <>
          <hr />
          <Tags
            icon="heart-ol"
            iconCommonality="heart"
            isMyProfile={isMyProfile}
            label="INTERESTS"
            link="/profile/settings/interests"
            myTags={interests}
            userTags={user.interests}
          />
        </>
      )}

      {canViewSkillsField(organization) && !isEducational && user.skills.length !== 0 && (
        <>
          <hr />
          <Tags
            icon="hammer"
            iconCommonality="hammer-filled"
            isMyProfile={isMyProfile}
            label="SKILLS"
            link="/profile/settings/skills"
            myTags={skills}
            userTags={user.skills}
          />
        </>
      )}

      {isEducational && user.clubs.length !== 0 && (
        <>
          <hr />
          <Tags
            icon="ios-people-outline"
            iconCommonality="ios-people"
            isMyProfile={isMyProfile}
            label="CLUBS"
            link="/profile/settings/clubs"
            myTags={clubs}
            userTags={user.clubs}
          />
        </>
      )}
    </div>
  );
};

export default PersonLeftInfo;
